import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UsageRightPaymentModel, UsageRightState } from '@markant-platform/usage-right/model';
import { endOfDay, startOfDay } from '@markant-platform/shared/util-helpers';

export const INVALID_DATE_VALUE = 'Invalid Date';

export enum UsageRightFormControls {
  state = 'state',
  paymentModel = 'paymentModel',
  boughtAt = 'boughtAt',
  validityPeriodStart = 'validityPeriodStart',
  validityPeriodEnd = 'validityPeriodEnd',
}

export type UsageRightFormControlsType = {
  [UsageRightFormControls.state]: FormControl<UsageRightState>;
  [UsageRightFormControls.paymentModel]: FormControl<UsageRightPaymentModel>;
  [UsageRightFormControls.boughtAt]: FormControl<string>;
  [UsageRightFormControls.validityPeriodStart]: FormControl<string>;
  [UsageRightFormControls.validityPeriodEnd]: FormControl<string>;
};

export const USAGE_RIGHT_FORM: () => FormGroup<UsageRightFormControlsType> = () => {
  return new FormGroup(
    {
      state: new FormControl<UsageRightState>(UsageRightState.BOOKED, {
        nonNullable: true,
        validators: Validators.required,
      }),
      paymentModel: new FormControl<UsageRightPaymentModel>(
        {
          value: UsageRightPaymentModel.SUBSCRIPTION,
          disabled: true,
        },
        {
          nonNullable: true,
          validators: Validators.required,
        }
      ),
      boughtAt: new FormControl<string>(
        {
          value: '',
          disabled: true,
        },
        {
          nonNullable: true,
          validators: [Validators.required, validateDate],
        }
      ),
      validityPeriodStart: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, validateDate],
      }),
      validityPeriodEnd: new FormControl<string>('', { nonNullable: true, validators: validateDate }),
    },
    { validators: [validateStartBeforeEnd] }
  );
};

//Converts Date to UTC format disregarding current timezone
export function parseDate(date: string): Date {
  const [day, month, year] = date.split('.');

  return new Date(Date.UTC(+year, +month - 1, +day));
}

function validateDate(control: AbstractControl): ValidationErrors | null {
  const { dirty, value } = control;

  if (!dirty || !value) return null;

  // If it can't be parsed. We don't recognize it as a date
  return !checkIsDateValid(parseDate(value)) ? { notADate: true } : null;
}

function checkIsDateValid(date: Date): boolean {
  return date.toString() !== INVALID_DATE_VALUE;
}

function validateStartBeforeEnd(formGroup: AbstractControl): ValidationErrors | null {
  const { dirty, controls } = formGroup as FormGroup;
  const { validityPeriodStart, validityPeriodEnd } = controls;

  if (!dirty || !(validityPeriodStart.value && validityPeriodEnd.value)) return null;

  const startDate: Date = startOfDay(parseDate(validityPeriodStart.value));
  const endDate: Date = endOfDay(parseDate(validityPeriodEnd.value));

  if (!checkIsDateValid(startDate) || !checkIsDateValid(endDate) || startDate >= endDate) {
    return { startDateBeforeEndDate: true };
  }

  return null;
}
